/**
 * Talking to an agent, or to one of your groups: the empty page (the reference's greeting, composer,
 * quick actions and shelves), the conversation once you write, and the panel beside it with the
 * agent's routines, memory and settings.
 *
 * An agent does not narrate. Given a job it says so, works out of sight — reading, computing, writing
 * to other agents — and leaves the finished answer; what it did meanwhile stays behind one small line.
 */
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { ArrowLeft, ArrowLeftRight, AtSign, Brain, Calculator, ChartPie, Check, ChevronRight, Clock, CornerDownRight, FileText, Image as ImageIcon, Landmark, Mic, Paperclip, Pause, Pencil, Play, Plus, Search, SquareCode, Trash2, X } from 'lucide-react'
import { create } from 'zustand'
import { Corners, Nut, Plane, WorkspaceMark } from './icons'
import { Dictation, FileChip, toPending } from './Compose'
import type { TaskRun } from '@shared/types'
import { agentsApi, api, integrations, type Skill } from './api'
import { EXAMPLE, type Agent, type Group, type MemoryItem, type Routine, type Session } from './data'
import { groupQuick, quick as quickOf } from './words'
import { Btn, Card, Dialog, Field, Glyph, Glyphs, I, IconBtn, KeyCap, Menu, PALETTES, Rule, SectionHead, Sheet, Status, Switch, Tabs, cx, inputCls, menuOn, popAt, usd, type Icon, type Palette, type PopEntry } from './kit'
import { agentMenu, chatList, deleteAgent, disbandGroup, moveList, routineMenu, sessionMenu } from './menus'
import { MemoryDialog } from './Memory'
import { Empty, useTabs } from './Page'
import { useDemo, type Panel, type Pending } from './store'
import { Thread, WireView } from './Thread'

export function useMedia(query: string) {
  const [on, setOn] = useState(() => window.matchMedia(query).matches)
  useEffect(() => {
    const m = window.matchMedia(query)
    const f = () => setOn(m.matches)
    m.addEventListener('change', f)
    return () => m.removeEventListener('change', f)
  }, [query])
  return on
}

/** Who the agents screen is about: one agent, or one of your groups with its members. */
export function useTarget() {
  const id = useDemo((s) => s.agentId)
  const agents = useDemo((s) => s.agents)
  const groups = useDemo((s) => s.groups)
  const group = groups.find((g) => g.id === id)
  const agent = group ? undefined : (agents.find((a) => a.id === id) ?? agents[0])
  const members = group ? group.members.map((m) => agents.find((a) => a.id === m)).filter((a): a is Agent => !!a) : []
  return { id: group?.id ?? agent?.id ?? '', agent, group, members, name: group?.name ?? agent?.name ?? '' }
}

const clock = (ms: number) => new Date(ms).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
const day = (ms: number) => new Date(ms).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })
const paletteOf = (agents: Agent[], id: string): Palette => agents.find((a) => a.id === id)?.palette ?? 'syntax'
const span = (ms: number) => {
  const s = Math.max(1, Math.round(ms / 1000))
  return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, '0')}s`
}

/* ── the composer ────────────────────────────────────────────────────── */

/** What the composer's menus offer besides files: the stocks you hold, and your skills (a way of working written once, for every agent). */
const useExtras = create<{ held: string[]; skills: Skill[]; at: number }>(() => ({ held: [], skills: [], at: 0 }))
async function loadExtras() {
  if (Date.now() - useExtras.getState().at < 60_000) return
  useExtras.setState({ at: Date.now() })
  void integrations.skills().then((skills) => useExtras.setState({ skills }), () => {})
  if (useDemo.getState().brokers) void api.portfolioLive().then((p) => useExtras.setState({ held: [...p.holdings].sort((a, b) => (b.value ?? 0) - (a.value ?? 0)).slice(0, 8).map((h) => h.symbol) }), () => {})
}
const flipSkill = (id: string) => {
  const k = useExtras.getState().skills.find((x) => x.id === id)
  if (!k) return
  useExtras.setState((s) => ({ skills: s.skills.map((x) => (x.id === id ? { ...x, enabled: !k.enabled } : x)) }))
  void integrations.skillUpdate(id, { enabled: !k.enabled }).catch(() => useExtras.setState((s) => ({ skills: s.skills.map((x) => (x.id === id ? { ...x, enabled: k.enabled } : x)) })))
}

export function Composer({ target, agent, group, dock }: { target?: string; agent?: Agent; group?: Group; dock?: boolean }) {
  const empty = !!group && group.members.length === 0
  const send = useDemo((s) => s.send)
  const stop = useDemo((s) => s.stop)
  const agents = useDemo((s) => s.agents)
  const say = useDemo((s) => s.say)
  const maxFiles = useDemo((s) => s.user?.maxFiles ?? 10)
  // An answer is being written in the conversation this composer belongs to.
  const writing = useDemo((s) => s.streaming && !!s.live && (target ? s.current[target] === s.live.chatId : s.tempId === s.live.chatId))
  const [text, setText] = useState('')
  const [files, setFiles] = useState<Pending[]>([])
  const [listening, setListening] = useState(false)
  const ref = useRef<HTMLTextAreaElement>(null)
  const picker = useRef<HTMLInputElement>(null)
  const pictures = useRef<HTMLInputElement>(null)
  // What the "+" and "Skills" menus offer is read when one of them is first reached for.
  const { held, skills } = useExtras()
  const side = dock ? 'top' : 'bottom'

  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    el.style.height = '0px'
    el.style.height = `${Math.min(el.scrollHeight, 200)}px`
  }, [text])
  // A quick action (or a card's "edit in chat") can hand the composer its words.
  useEffect(() => {
    const fill = (e: Event) => {
      setText((e as CustomEvent<string>).detail)
      ref.current?.focus()
    }
    window.addEventListener('ws-fill', fill)
    return () => window.removeEventListener('ws-fill', fill)
  }, [])

  const submit = () => {
    if ((!text.trim() && !files.length) || empty || writing) return
    void send(text, target ? { agentId: target } : { temp: true }, files)
    setText('')
    setFiles([])
  }
  const add = (word: string) => setText((t) => `${t}${t && !t.endsWith(' ') ? ' ' : ''}${word} `)
  const attach = async (list: FileList | null) => {
    if (!list?.length) return
    const next = [...files]
    for (const f of Array.from(list)) {
      if (next.length >= maxFiles) {
        say(`At most ${maxFiles} files in one message`)
        break
      }
      try {
        next.push(await toPending(f))
      } catch (e) {
        say(e instanceof Error ? e.message : `${f.name} could not be read`)
      }
    }
    setFiles(next)
    if (picker.current) picker.current.value = ''
  }
  const mates = agents.filter((a) => a.id !== agent?.id && (!group || group.members.includes(a.id)))

  return (
    <div
      className="flex w-full flex-col rounded-[10px] border border-ws-focus bg-ws-panel transition-colors duration-150 focus-within:border-[#8a8a8a]"
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        e.preventDefault()
        void attach(e.dataTransfer.files)
      }}
    >
      <input ref={picker} type="file" multiple hidden onChange={(e) => void attach(e.target.files)} />
      <input ref={pictures} type="file" accept="image/*" multiple hidden onChange={(e) => void attach(e.target.files)} />
      {files.length > 0 && (
        <div className="flex flex-wrap gap-2 px-3 pt-3">
          {files.map((f, i) => (
            <FileChip key={i} name={f.name} onPanel onRemove={() => setFiles(files.filter((_, j) => j !== i))} />
          ))}
        </div>
      )}
      {listening ? (
        <Dictation
          base={text.trim()}
          dock={dock}
          onCancel={() => setListening(false)}
          onDone={(heard) => {
            setListening(false)
            if (heard) setText((t) => `${t.trim()}${t.trim() ? ' ' : ''}${heard}`)
            window.setTimeout(() => ref.current?.focus(), 0)
          }}
        />
      ) : (
        <>
          <textarea
            ref={ref}
            rows={1}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                e.preventDefault()
                submit()
              }
            }}
            onPaste={(e) => e.clipboardData.files.length && (e.preventDefault(), void attach(e.clipboardData.files))}
            disabled={empty}
            placeholder={empty ? `Add agents to ${group!.name} to start` : dock ? `Message ${group?.name ?? agent?.name ?? ''}`.trim() : agent ? EXAMPLE[agent.kind] : 'Ask anything...'}
            aria-label="Message"
            className={cx('block w-full resize-none px-4 pt-[18px] tracking-normal', dock ? 'min-h-[52px] pb-1' : 'min-h-[85px] pb-2')}
          />
          <div className="mx-2 mb-[9px] flex h-8 items-center">
            <div onPointerDown={() => void loadExtras()}>
              <Menu
                side={side}
                items={[
                  { label: 'File', icon: <I icon={Paperclip} className="text-ws-icon" />, disabled: empty, onSelect: () => picker.current?.click() },
                  { label: 'Picture', icon: <I icon={ImageIcon} className="text-ws-icon" />, disabled: empty, onSelect: () => pictures.current?.click() },
                  ...(held.length ? (['rule'] as const) : []),
                  // (a stock you hold, named in the message so the agent knows which one you mean)
                  ...held.map((sym) => ({ label: sym, icon: <I icon={ChartPie} className="text-ws-icon" />, hint: 'Holding', onSelect: () => add(`$${sym}`) })),
                ]}
                trigger={() => <IconBtn icon={Plus} label="Add" onPanel />}
              />
            </div>
            {mates.length > 0 && <Menu side={side} className="ml-2" items={mates.map((a) => ({ label: a.name, icon: <Glyph palette={a.palette} />, hint: a.label, onSelect: () => add(`@${a.name}`) }))} trigger={() => <IconBtn icon={AtSign} label="Mention an agent" onPanel />} />}
            <span className="ml-[7px] h-4 w-px shrink-0 bg-ws-rule" />
            <div onPointerDown={() => void loadExtras()}>
              <Menu
                side={side}
                keep
                width={240}
                className="ml-[9px]"
                items={
                  skills.length
                    ? skills.map((k) => ({ label: k.name, checked: k.enabled, onSelect: () => flipSkill(k.id) }))
                    : [{ label: 'Write a skill', hint: 'Settings', onSelect: () => (useDemo.getState().go('settings'), useTabs.getState().set({ settings: 'general' })) }]
                }
                trigger={() => (
                  <Btn icon={SquareCode} onPanel>
                    Skills
                  </Btn>
                )}
              />
            </div>
            <IconBtn icon={Mic} label="Speak instead of typing" onPanel className="ml-auto" disabled={empty} onClick={() => setListening(true)} />
            {writing ? (
              <button type="button" aria-label="Stop" title="Stop" onClick={stop} className="ml-[7px] flex h-8 w-8 shrink-0 -translate-y-px items-center justify-center rounded-[8px] bg-white text-black">
                <span className="block h-[10px] w-[10px] rounded-[2px] bg-black" />
              </button>
            ) : (
              <button type="button" aria-label="Send" disabled={(!text.trim() && !files.length) || empty} onClick={submit} className="ml-[7px] flex h-8 w-8 shrink-0 -translate-y-px items-center justify-center rounded-[8px] bg-white text-black disabled:opacity-40">
                <Plane size={16} />
              </button>
            )}
          </div>
        </>
      )}
    </div>
  )
}

/* ── the empty page ──────────────────────────────────────────────────── */

const greeting = () => {
  const h = new Date().getHours()
  return h < 5 ? 'Good night' : h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening'
}
const QUICK_ICON: Icon[] = [ChartPie, Search, Landmark, FileText, Brain]
export const fillComposer = (text: string) => window.dispatchEvent(new CustomEvent('ws-fill', { detail: text }))

function Shelf({ title, count, actions, children }: { title: string; count?: number; actions?: (all: boolean, flip: () => void) => ReactNode; children: ReactNode[] }) {
  const [all, setAll] = useState(false)
  return (
    <div>
      <SectionHead title={title} count={count}>
        {actions?.(all, () => setAll(!all))}
        {children.length > 4 && (
          <>
            {actions && <Rule className="ml-[9px] hidden lg:block" />}
            <Btn icon={Corners} className="ml-[9px] hidden lg:flex" onClick={() => setAll(!all)}>
              {all ? 'Collapse' : 'Expand'}
            </Btn>
          </>
        )}
      </SectionHead>
      <div className={cx('ws-noscroll mt-5 flex gap-3 overflow-x-auto pb-px lg:grid lg:grid-cols-4 lg:overflow-visible', !all && 'lg:[&>*:nth-child(n+5)]:hidden')}>{children}</div>
    </div>
  )
}

/** The reference's app card: a tile, a name, something on the right, two quiet lines. */
function Tall({ tile, name, right, about, onClick, menu }: { tile: ReactNode; name: string; right?: ReactNode; about: string; onClick: () => void; menu?: () => PopEntry[] }) {
  return (
    <Card as="button" onClick={onClick} menu={menu} className="h-[94px] w-[260px] shrink-0 pl-3 pr-2 pt-[10px] lg:w-auto">
      <span className="flex h-7 items-center">
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-[6px] bg-ws-bg">{tile}</span>
        <span className="ml-3 min-w-0 truncate font-medium text-ws-fg">{name}</span>
        {right && <span className="ml-auto shrink-0 pl-2 pr-1">{right}</span>}
      </span>
      <span className="mt-[10px] line-clamp-2 whitespace-pre-line text-[12px] leading-[15px] tracking-normal text-ws-fg-3">{about}</span>
    </Card>
  )
}

const dated = (ms: number) => new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
/** A line of text with the words that were looked for lit up. */
function Lit({ text, needle }: { text: string; needle: string }) {
  const i = needle ? text.toLocaleLowerCase('tr').indexOf(needle) : -1
  if (i < 0) return <>{text}</>
  return (
    <>
      {text.slice(0, i)}
      <span className="text-ws-fg">{text.slice(i, i + needle.length)}</span>
      {text.slice(i + needle.length)}
    </>
  )
}

/**
 * An agent's past conversations. The latest few stand as cards; Search goes through all of them —
 * their titles and every word said in them — because with a hundred conversations a row of four
 * cards finds nothing.
 */
function PastChats({ id, list }: { id: string; list: Session[] }) {
  const agents = useDemo((s) => s.agents)
  const isAgent = agents.some((a) => a.id === id)
  const openSession = useDemo((s) => s.openSession)
  const [q, setQ] = useState<string | null>(null)
  const [all, setAll] = useState(false)
  const [found, setFound] = useState<{ chatId: string; title: string; snippet: string; at: number }[] | null>(null)
  const needle = (q ?? '').trim().toLocaleLowerCase('tr')
  // Titles are searched at once on what is loaded; the words said are searched on the server.
  useEffect(() => {
    if (needle.length < 2) return setFound(null)
    if (!isAgent) return setFound(list.filter((c) => c.title.toLocaleLowerCase('tr').includes(needle)).map((c) => ({ chatId: c.id, title: c.title, snippet: c.preview, at: c.at })))
    let on = true
    const t = window.setTimeout(() => {
      agentsApi
        .search(id, needle)
        .then((r) => on && setFound(r))
        .catch(() => on && setFound([]))
    }, 220)
    return () => {
      on = false
      window.clearTimeout(t)
    }
  }, [needle, id, isAgent]) // eslint-disable-line react-hooks/exhaustive-deps
  const hits = needle.length >= 2 ? found : null
  const grid = 'ws-noscroll mt-5 flex gap-3 overflow-x-auto pb-px lg:grid lg:grid-cols-4 lg:overflow-visible'
  const top = (at: number, c?: Session) => (
    <span className="flex h-4 items-center">
      {c?.busy ? <Status tone="busy">Working</Status> : c?.unread ? <Status tone="free">New</Status> : c?.wire ? <Glyphs palettes={[paletteOf(agents, c.wire.from), paletteOf(agents, c.wire.to)]} max={2} /> : <Glyphs palettes={[paletteOf(agents, id)]} max={1} />}
      <span className="ml-auto pl-2 pr-1 text-[12px] leading-4 tracking-normal text-ws-fg-4">{dated(at)}</span>
    </span>
  )
  return (
    <div>
      <SectionHead title="Previous chats" count={hits ? hits.length : list.length}>
        {q === null ? (
          <Btn icon={Search} gap={9} onClick={() => setQ('')}>
            Search
          </Btn>
        ) : (
          <label className="flex h-8 w-[240px] items-center gap-2 rounded-[8px] border border-ws-hair pl-[7px] pr-1 transition-colors duration-150 focus-within:border-ws-focus max-sm:w-[170px]">
            <I icon={Search} className="text-ws-icon" />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Escape' && setQ(null)} placeholder="Search these chats" aria-label="Search previous chats" className="h-full min-w-0 flex-1 tracking-normal" />
            <button type="button" aria-label="Close search" onClick={() => setQ(null)} className="flex h-6 w-6 shrink-0 items-center justify-center rounded-[6px] text-ws-icon transition-colors hover:bg-ws-hover hover:text-ws-fg">
              <X size={13} strokeWidth={2} />
            </button>
          </label>
        )}
        {!hits && list.length > 4 && (
          <>
            <Rule className="ml-[9px] hidden lg:block" />
            <Btn icon={Corners} className="ml-[9px] hidden lg:flex" onClick={() => setAll(!all)}>
              {all ? 'Collapse' : 'Expand'}
            </Btn>
          </>
        )}
      </SectionHead>
      {needle.length >= 2 ? (
        hits == null ? (
          <p className="m-0 mt-5 pl-2 text-[12px] leading-4 tracking-normal text-ws-fg-3">Searching…</p>
        ) : hits.length ? (
          <div className={grid}>
            {hits.map((h) => (
              <Card key={h.chatId} as="button" onClick={() => openSession(id, h.chatId)} menu={() => sessionMenu(h.chatId)} className="h-[94px] w-[260px] shrink-0 pl-3 pr-2 pt-3 lg:w-auto">
                {top(h.at || list.find((c) => c.id === h.chatId)?.at || Date.now())}
                <span className="mt-[9px] block truncate text-[12px] leading-4 tracking-normal text-ws-fg-2">
                  <Lit text={h.title} needle={needle} />
                </span>
                <span className="mt-[3px] line-clamp-2 text-[12px] leading-[15px] tracking-normal text-ws-fg-4">
                  <Lit text={h.snippet} needle={needle} />
                </span>
              </Card>
            ))}
          </div>
        ) : (
          <p className="m-0 mt-5 pl-2 text-[12px] leading-4 tracking-normal text-ws-fg-3">No conversation mentions “{q?.trim()}”.</p>
        )
      ) : (
        <div className={cx(grid, !all && 'lg:[&>*:nth-child(n+5)]:hidden')}>
          {list.map((c) => (
            <Card key={c.id} as="button" onClick={() => openSession(id, c.id)} menu={() => sessionMenu(c.id)} className="h-[94px] w-[260px] shrink-0 pl-3 pr-2 pt-3 lg:w-auto">
              {top(c.at, c)}
              <span className="mt-[9px] block truncate text-[12px] leading-4 tracking-normal text-ws-fg-2">{c.title}</span>
              <span className="mt-[3px] line-clamp-2 text-[12px] leading-[15px] tracking-normal text-ws-fg-4">{c.preview}</span>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}

export function AgentHome() {
  const { id, agent, group, members, name } = useTarget()
  const sessions = useDemo((s) => s.sessions)
  const agents = useDemo((s) => s.agents)
  const routines = useDemo((s) => s.routines)
  const openAgent = useDemo((s) => s.openAgent)
  const setS = useDemo((s) => s.set)
  const firstName = useDemo((s) => (s.user?.name || s.user?.username || '').split(' ')[0])
  const mine = sessions.filter((x) => x.agentId === id).sort((a, b) => Number(b.pinned) - Number(a.pinned) || b.at - a.at)
  const own = agent ? routines.filter((r) => r.agentId === agent.id) : []
  const quick = agent ? quickOf(agent.kind) : groupQuick()

  return (
    <div className="flex min-h-full flex-col">
      <section className="flex flex-col items-center px-4 md:px-0">
        <h1 className="m-0 mt-10 text-center font-ws-serif text-[28px] font-normal leading-[36px] tracking-normal text-ws-fg md:mt-[78px] md:text-[33px] md:leading-[40px]">{greeting()}{firstName ? `, ${firstName}` : ''}</h1>
        <p className="m-0 mt-3 text-center text-[12px] leading-4 tracking-normal text-ws-fg-3 md:mt-4">{group ? (members.length ? `${name} is here, where should we start today?` : `${name} has no agents yet`) : `I’m ${name}, where should we start today?`}</p>
        <div className="mt-7 w-full max-w-[800px] md:mt-9">
          <Composer target={id} agent={agent} group={group} />
        </div>
        <div className="ws-noscroll -mx-4 mt-4 flex w-[calc(100%+32px)] max-w-none gap-2 overflow-x-auto px-4 md:mx-0 md:mt-[27px] md:w-full md:max-w-[800px] md:flex-wrap md:overflow-visible md:px-0">
          {quick.map((q, i) => (
            <button key={q} type="button" onClick={() => fillComposer(q)} className="flex h-8 shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-[8px] border border-ws-hair bg-ws-well pl-[7px] pr-[10px] transition-colors duration-150 hover:border-ws-edge hover:bg-ws-hover hover:text-ws-fg md:flex-auto">
              <I icon={QUICK_ICON[i % QUICK_ICON.length]} className="text-ws-icon" />
              {q}
            </button>
          ))}
        </div>
      </section>

      <section className="mt-auto space-y-8 px-4 pb-6 pt-12 md:space-y-[45px] md:px-10 md:pb-[39px]">
        {mine.length > 0 && <PastChats id={id} list={mine} />}

        {group && (
          <Shelf
            title="Members"
            count={members.length}
            actions={() => (
              <Btn icon={Nut} onClick={() => setS({ panel: 'settings' })}>
                Manage
              </Btn>
            )}
          >
            {members.length
              ? members.map((a) => <Tall key={a.id} tile={<Glyph palette={a.palette} />} name={a.name} right={<Status tone={a.busy ? 'busy' : 'free'}>{a.busy ? 'Busy' : 'Free'}</Status>} about={a.busy ?? a.description} onClick={() => openAgent(a.id)} menu={() => agentMenu(a.id)} />)
              : [<Tall key="add" tile={<I icon={Plus} className="text-ws-icon" />} name="Add agents" about="Choose who takes part in this group chat." onClick={() => setS({ panel: 'settings' })} />]}
          </Shelf>
        )}

        {own.length > 0 && (
          <Shelf title="Routines" count={own.length}>
            {own.map((r) => (
              <Tall key={r.id} tile={<I icon={Clock} className="text-ws-icon" />} name={r.name} right={!r.on && <Status tone="off">Paused</Status>} about={`${r.when}\nLast run: ${r.last}`} onClick={() => setS({ panel: 'routines', routineId: r.id })} menu={() => routineMenu(r.id)} />
            ))}
          </Shelf>
        )}
      </section>
    </div>
  )
}

/* ── the panel beside an agent ───────────────────────────────────────── */

const label = 'm-0 text-[12px] font-normal leading-4 tracking-normal text-ws-label'

const RUN: Record<TaskRun['status'], string> = { ok: 'Done', quiet: 'Nothing to report', skipped: 'Skipped', error: 'Did not work' }

export function RoutineDetail({ routine, agent }: { routine: Routine; agent: Agent }) {
  const setS = useDemo((s) => s.set)
  const patch = useDemo((s) => s.patchRoutine)
  const remove = useDemo((s) => s.removeRoutine)
  const toggle = useDemo((s) => s.toggleRoutine)
  const [edit, setEdit] = useState(false)
  const [draft, setDraft] = useState(routine)
  useEffect(() => (setDraft(routine), setEdit(false)), [routine.id]) // eslint-disable-line react-hooks/exhaustive-deps
  const openChat = useDemo((s) => s.openChat)
  const [runs, setRuns] = useState<TaskRun[] | null>(null)
  useEffect(() => {
    let on = true
    setRuns(null)
    void api.taskRuns(routine.id).then((x) => on && setRuns(x), () => on && setRuns([]))
    return () => {
      on = false
    }
  }, [routine.id, routine.last])
  return (
    <div>
      <button type="button" onClick={() => setS({ routineId: null })} className="-ml-[6px] flex h-7 items-center gap-[6px] rounded-[6px] px-[6px] text-[12px] tracking-normal text-ws-fg-3 transition-colors hover:bg-ws-hover hover:text-ws-fg">
        <ArrowLeft size={13} strokeWidth={2} />
        Routines
      </button>
      {edit ? (
        <div className="mt-3 space-y-4">
          <Field label="Name">
            <input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} className={inputCls} />
          </Field>
          <Field label="Instruction">
            <textarea value={draft.instruction} onChange={(e) => setDraft({ ...draft, instruction: e.target.value })} rows={10} className={cx(inputCls, 'h-auto resize-none py-2 text-[13px] leading-[19px] tracking-normal')} />
          </Field>
          <Field label="When to run" hint={`Ask ${agent.name} to change it`}>
            <div className={cx(inputCls, 'flex items-center text-ws-fg-3')}>{routine.when}</div>
          </Field>
          <div className="flex gap-2">
            <Btn variant="white" disabled={!draft.name.trim()} onClick={() => (patch(routine.id, { name: draft.name.trim(), instruction: draft.instruction }), setEdit(false))}>
              Save
            </Btn>
            <Btn variant="outline" onClick={() => (setDraft(routine), setEdit(false))}>
              Cancel
            </Btn>
          </div>
        </div>
      ) : (
        <>
          <h3 className="m-0 mt-3 font-medium leading-5 text-ws-fg">{routine.name}</h3>
          <div className="mt-1 flex items-center gap-[6px] text-[12px] tracking-normal text-ws-fg-3">
            <Glyph palette={agent.palette} size={14} />
            {agent.name}
            {!routine.on && (
              <Status tone="off" className="ml-2">
                Paused
              </Status>
            )}
          </div>
          <h4 className={cx(label, 'mt-5')}>Instruction</h4>
          <p className="m-0 mt-2 whitespace-pre-wrap rounded-[8px] border border-ws-hair bg-ws-well px-3 py-[10px] text-[13px] leading-[19px] tracking-normal text-ws-fg-2">{routine.instruction}</p>
          <h4 className={cx(label, 'mt-5')}>When to run</h4>
          <dl className="m-0 mt-2 divide-y divide-ws-hair rounded-[8px] border border-ws-hair bg-ws-well px-3 text-[13px] tracking-normal">
            {[
              ['Schedule', routine.when],
              ['Next run', routine.on ? routine.next : '—'],
              ['Last run', routine.last],
            ].map(([k, v]) => (
              <div key={k} className="flex h-9 items-center justify-between gap-3">
                <dt className="shrink-0 text-ws-fg-3">{k}</dt>
                <dd className="m-0 truncate text-ws-fg-2">{v}</dd>
              </div>
            ))}
          </dl>
          {/* what each run came to; pressed, a run opens the conversation it wrote in */}
          <h4 className={cx(label, 'mt-5')}>What it did</h4>
          <div className="mt-2 divide-y divide-ws-hair overflow-hidden rounded-[8px] border border-ws-hair bg-ws-well text-[13px] tracking-normal">
            {(runs ?? []).slice(0, 8).map((r) => (
              <button key={r.id} type="button" disabled={!r.chatId} onClick={() => r.chatId && void openChat(r.chatId)} className="flex min-h-9 w-full items-center gap-3 px-3 py-[6px] text-left transition-colors duration-150 enabled:hover:bg-ws-hover">
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-ws-fg-2">{r.summary || RUN[r.status]}</span>
                  <span className="block text-[12px] leading-4 text-ws-fg-4">
                    {new Date(r.createdAt).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })} · {RUN[r.status]}
                  </span>
                </span>
                {r.chatId && <ChevronRight size={14} strokeWidth={2} className="shrink-0 text-ws-fg-4" />}
              </button>
            ))}
            {runs && !runs.length && <div className="flex h-9 items-center px-3 text-ws-fg-4">It has not run yet.</div>}
            {!runs && <div className="h-9" />}
          </div>
          <div className="mt-5 flex items-center gap-2">
            <Btn variant="outline" icon={routine.on ? Pause : Play} iconSize={14} onClick={() => toggle(routine.id)}>
              {routine.on ? 'Pause' : 'Resume'}
            </Btn>
            <Btn variant="outline" icon={Pencil} iconSize={14} onClick={() => setEdit(true)}>
              Edit
            </Btn>
            <IconBtn icon={Trash2} size={15} label="Delete this routine" className="ml-auto hover:text-ws-down" onClick={() => remove(routine.id)} />
          </div>
        </>
      )}
    </div>
  )
}

function RoutinesTab({ agent }: { agent: Agent }) {
  const all = useDemo((s) => s.routines)
  const routineId = useDemo((s) => s.routineId)
  const setS = useDemo((s) => s.set)
  const routines = all.filter((r) => r.agentId === agent.id)
  const open = routines.find((r) => r.id === routineId)
  if (open) return <RoutineDetail routine={open} agent={agent} />
  return (
    <div>
      {routines.length > 0 ? (
        <div className="divide-y divide-ws-edge overflow-hidden rounded-[10px] border border-ws-edge bg-ws-panel">
          {routines.map((r) => (
            <button key={r.id} type="button" onClick={() => setS({ routineId: r.id })} className="flex w-full items-center gap-3 px-3 py-[10px] text-left transition-colors duration-150 hover:bg-[#1c1c1c]">
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[13px] leading-[18px] tracking-normal text-ws-fg">{r.name}</span>
                <span className="mt-px block truncate text-[12px] leading-4 tracking-normal text-ws-fg-3">{r.on ? r.when : 'Paused'}</span>
              </span>
              <ChevronRight size={14} strokeWidth={2} className="shrink-0 text-ws-fg-4" />
            </button>
          ))}
        </div>
      ) : (
        <p className="m-0 text-[12px] leading-4 tracking-normal text-ws-fg-3">No routines yet.</p>
      )}
      <Btn
        icon={Plus}
        className="-ml-2 mt-3"
        onClick={() => {
          fillComposer('Her sabah 09:00’da ')
          if (!window.matchMedia('(min-width: 1280px)').matches) setS({ panel: null })
        }}
      >
        Ask {agent.name} for a routine
      </Btn>
    </div>
  )
}

const MEMORY_GROUPS: { kind: MemoryItem['kind']; title: string }[] = [
  { kind: 'rule', title: 'Standing rules' },
  { kind: 'about', title: 'About you' },
  { kind: 'learned', title: 'Learned from outcomes' },
  { kind: 'note', title: 'Working notes' },
]

function MemoryTab({ agent }: { agent: Agent }) {
  const remember = useDemo((s) => s.remember)
  const forget = useDemo((s) => s.forget)
  const edit = useDemo((s) => s.editMemory)
  const shared = useDemo((s) => s.shared.filter((n) => !n.off).length)
  const go = useDemo((s) => s.go)
  const [text, setText] = useState('')
  const [openId, setOpenId] = useState<string | null>(null)
  const open = agent.memory.find((x) => x.id === openId)
  return (
    <div className="space-y-5">
      {/* the layer under this one: what every agent knows */}
      <button type="button" onClick={() => (go('settings'), useTabs.getState().set({ settings: 'memory' }))} className="flex min-h-10 w-full items-center gap-3 rounded-[8px] border border-ws-line px-3 py-[6px] text-left transition-colors duration-150 hover:bg-ws-hover">
        <span className="min-w-0 flex-1">
          <span className="block text-[13px] leading-[18px] tracking-normal text-ws-fg">Shared memory</span>
          <span className="block text-[12px] leading-4 tracking-normal text-ws-fg-3">{shared} things every agent knows</span>
        </span>
        <ChevronRight size={14} strokeWidth={2} className="shrink-0 text-ws-fg-4" />
      </button>
      {MEMORY_GROUPS.map((g) => {
        const items = agent.memory.filter((x) => x.kind === g.kind)
        if (!items.length) return null
        return (
          <section key={g.kind}>
            <h3 className={label}>{g.title}</h3>
            <div className="mt-2 divide-y divide-ws-hair overflow-hidden rounded-[8px] border border-ws-line">
              {items.map((it) => (
                <button key={it.id} type="button" onClick={() => setOpenId(it.id)} className="block w-full px-3 py-2 text-left transition-colors duration-150 hover:bg-ws-hover">
                  <span className="line-clamp-2 text-[13px] leading-[19px] tracking-normal text-ws-fg-2">{it.text}</span>
                  {it.when && <span className="mt-px block text-[12px] leading-4 tracking-normal text-ws-fg-4">Updated {it.when}</span>}
                </button>
              ))}
            </div>
          </section>
        )
      })}
      <form
        className="flex h-9 items-center rounded-[8px] border border-ws-line bg-ws-field pl-[10px] pr-1 transition-colors duration-150 focus-within:border-ws-focus"
        onSubmit={(e) => {
          e.preventDefault()
          if (!text.trim()) return
          remember(agent.id, text.trim(), 'rule')
          setText('')
        }}
      >
        <input value={text} onChange={(e) => setText(e.target.value)} placeholder={`Teach ${agent.name} something`} className="h-full min-w-0 flex-1 tracking-normal" aria-label="New memory" />
        <button type="submit" disabled={!text.trim()} className="flex h-7 shrink-0 items-center rounded-[6px] px-2 text-[12px] tracking-normal text-ws-fg-2 transition-colors hover:bg-ws-raise hover:text-ws-fg disabled:text-ws-fg-5 disabled:hover:bg-transparent">
          Remember
        </button>
      </form>
      <MemoryDialog open={!!open} title={`${agent.name} remembers`} text={open?.text ?? ''} onClose={() => setOpenId(null)} onSave={(v) => open && edit(agent.id, open.id, v.text)} onForget={() => open && forget(agent.id, open.id)} />
    </div>
  )
}

/** One of two: a pressed half and a quiet half. */
export function Pick<T extends string>({ options, value, onChange }: { options: T[]; value: T; onChange: (v: T) => void }) {
  return (
    <div className="flex h-9 gap-px rounded-[8px] border border-ws-line bg-ws-field p-[3px]">
      {options.map((o) => (
        <button key={o} type="button" onClick={() => onChange(o)} className={cx('flex-1 rounded-[5px] transition-colors duration-150', value === o ? 'bg-ws-raise text-ws-fg' : 'text-ws-fg-3 hover:text-ws-fg')}>
          {o}
        </button>
      ))}
    </div>
  )
}

function Faces({ value, onChange, onPanel }: { value: Palette; onChange: (p: Palette) => void; onPanel?: boolean }) {
  return (
    <div className="flex flex-wrap gap-2">
      {PALETTES.map((p) => (
        <button key={p} type="button" aria-label={p} aria-pressed={value === p} onClick={() => onChange(p)} className={cx('flex h-8 w-8 items-center justify-center rounded-[8px] border transition-colors duration-150', value === p ? 'border-ws-focus bg-ws-panel' : cx('border-ws-line', onPanel ? 'hover:bg-ws-raise' : 'hover:bg-ws-hover'))}>
          <Glyph palette={p} />
        </button>
      ))}
    </div>
  )
}

/** A row you switch on or off, in a framed list. */
export function Rows({ children }: { children: ReactNode }) {
  return <div className="divide-y divide-ws-hair rounded-[8px] border border-ws-line">{children}</div>
}
export function Row({ left, title, sub, right }: { left?: ReactNode; title: string; sub?: string; right: ReactNode }) {
  return (
    <div className="flex min-h-10 items-center gap-3 px-3 py-[6px]">
      {left}
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[13px] leading-[18px] tracking-normal text-ws-fg">{title}</span>
        {sub && <span className="block text-[12px] leading-4 tracking-normal text-ws-fg-3">{sub}</span>}
      </span>
      {right}
    </div>
  )
}

function SettingsTab({ agent }: { agent: Agent }) {
  const patch = useDemo((s) => s.patchAgent)
  const groups = useDemo((s) => s.groups)
  const folders = useDemo((s) => s.folders)
  const toggleMember = useDemo((s) => s.toggleMember)
  const folder = folders.find((f) => f.members.includes(agent.id))
  const chats = groups.filter((g) => g.members.includes(agent.id))
  const quiet = 'shrink-0 rounded-[6px] px-2 py-1 text-[12px] tracking-normal text-ws-fg-3 transition-colors duration-150 hover:bg-ws-hover hover:text-ws-fg'
  return (
    <div className="space-y-4">
      <Field label="Name">
        <input value={agent.name} onChange={(e) => patch(agent.id, { name: e.target.value })} className={inputCls} />
      </Field>
      <Field label="Label" hint="optional">
        <input value={agent.label} onChange={(e) => patch(agent.id, { label: e.target.value })} className={inputCls} />
      </Field>
      <Field label="Description">
        <textarea value={agent.description} onChange={(e) => patch(agent.id, { description: e.target.value })} rows={3} className={cx(inputCls, 'h-auto resize-none py-2 leading-[20px]')} />
      </Field>
      <Field label="Face">
        <Faces value={agent.palette} onChange={(p) => patch(agent.id, { palette: p })} />
      </Field>
      <Field label="Language">
        <Pick options={['Türkçe', 'English']} value={agent.lang} onChange={(v) => patch(agent.id, { lang: v })} />
      </Field>
      <Rows>
        <Row title="Notifications" sub={agent.notify ? 'Reaches you when a job is done' : 'Stays silent'} right={<Switch on={agent.notify} onChange={(v) => patch(agent.id, { notify: v })} label="Notifications" />} />
      </Rows>
      {/* where it is kept and who it talks with: only its own, never the whole list */}
      <div>
        <div className={cx(label, 'mb-[6px]')}>Folder</div>
        <Rows>
          <Row
            title={folder?.name ?? 'Not in a folder'}
            right={
              <button type="button" onClick={(e) => popAt(e.currentTarget, () => moveList(agent.id))} className={cx(quiet, '-mr-1')}>
                Move
              </button>
            }
          />
        </Rows>
      </div>
      <div>
        <div className={cx(label, 'mb-[6px]')}>Group chats</div>
        {chats.length > 0 && (
          <Rows>
            {chats.map((g) => (
              <Row
                key={g.id}
                title={g.name}
                sub={`${g.members.length} ${g.members.length === 1 ? 'agent' : 'agents'}`}
                right={
                  <button type="button" onClick={() => toggleMember(g.id, agent.id)} className={cx(quiet, '-mr-1')}>
                    Leave
                  </button>
                }
              />
            ))}
          </Rows>
        )}
        <Btn icon={Plus} className={cx('-ml-2', chats.length > 0 && 'mt-1')} onClick={(e) => popAt(e.currentTarget, () => chatList(agent.id, 'others'))}>
          Add to a group chat
        </Btn>
      </div>
      <div className="pt-1">
        <Btn variant="danger" onClick={() => deleteAgent(agent.id)}>
          Delete {agent.name}
        </Btn>
      </div>
    </div>
  )
}

function GroupSettings({ group }: { group: Group }) {
  const agents = useDemo((s) => s.agents)
  const patch = useDemo((s) => s.patchGroup)
  const toggleMember = useDemo((s) => s.toggleMember)
  return (
    <div className="space-y-4">
      <Field label="Name">
        <input value={group.name} onChange={(e) => patch(group.id, { name: e.target.value })} className={inputCls} />
      </Field>
      <Field label="Members" hint={`${group.members.length} of ${agents.length}`}>
        <Rows>
          {agents.map((a) => (
            <Row key={a.id} left={<Glyph palette={a.palette} />} title={a.name} sub={a.label || undefined} right={<Switch on={group.members.includes(a.id)} onChange={() => toggleMember(group.id, a.id)} label={`${a.name} in ${group.name}`} />} />
          ))}
        </Rows>
      </Field>
      <div className="pt-1">
        <Btn variant="danger" onClick={() => disbandGroup(group.id)}>
          Disband group chat
        </Btn>
      </div>
    </div>
  )
}

const PANELS: { id: Panel; label: string }[] = [
  { id: 'routines', label: 'Routines' },
  { id: 'memory', label: 'Memory' },
  { id: 'settings', label: 'Settings' },
]

export function Inspector() {
  const panel = useDemo((s) => s.panel)
  const view = useDemo((s) => s.view)
  const setS = useDemo((s) => s.set)
  const { agent, group } = useTarget()
  const wide = useMedia('(min-width: 1280px)')
  if (!panel || view !== 'agents' || (!agent && !group)) return null
  const body = (
    <>
      <div className="flex h-12 shrink-0 items-center gap-2 border-b border-ws-line pl-2 pr-2">
        {agent ? (
          <Tabs options={PANELS} value={panel} onChange={(p) => setS({ panel: p, routineId: null })} className="flex-1" />
        ) : (
          <span className="flex min-w-0 flex-1 items-center gap-2 pl-2 font-medium text-ws-fg">
            <span className="truncate">{group!.name}</span>
          </span>
        )}
        <IconBtn icon={X} label="Close" onClick={() => setS({ panel: null, routineId: null })} />
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto p-4 pb-[max(16px,env(safe-area-inset-bottom))]">
        {group && <GroupSettings group={group} />}
        {agent && panel === 'routines' && <RoutinesTab agent={agent} />}
        {agent && panel === 'memory' && <MemoryTab agent={agent} />}
        {agent && panel === 'settings' && <SettingsTab agent={agent} />}
      </div>
    </>
  )
  return wide ? (
    <aside className="flex w-[360px] shrink-0 animate-ws-fade flex-col border-l border-ws-line">{body}</aside>
  ) : (
    <Sheet open side="right" onClose={() => setS({ panel: null, routineId: null })} className="flex w-full max-w-[440px] flex-col border-l border-ws-line">
      {body}
    </Sheet>
  )
}

/* ── a new agent, a folder, a new group chat ─────────────────────────── */

/** A choice among your own things, pressed in or not. */
function Chip({ on, onClick, children }: { on: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button type="button" aria-pressed={on} onClick={onClick} className={cx('flex h-8 items-center gap-2 rounded-[8px] border pl-2 pr-3 transition-colors duration-150', on ? 'border-ws-focus bg-ws-panel text-ws-fg' : 'border-ws-line text-ws-fg-2 hover:bg-ws-raise hover:text-ws-fg')}>
      {children}
    </button>
  )
}

export function AddAgent() {
  const open = useDemo((s) => s.addAgent)
  const setS = useDemo((s) => s.set)
  const add = useDemo((s) => s.addAgent_)
  const agents = useDemo((s) => s.agents)
  const [name, setName] = useState('')
  const [tag, setTag] = useState('')
  const [about, setAbout] = useState('')
  const [palette, setPalette] = useState<Palette>('amber')
  useEffect(() => {
    if (!open) return
    setName('')
    setTag('')
    setAbout('')
    setPalette(PALETTES.find((p) => !agents.some((a) => a.palette === p)) ?? 'amber')
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps
  const close = () => setS({ addAgent: false })
  const create = () => name.trim() && add({ name: name.trim(), label: tag.trim(), description: about.trim(), palette })
  return (
    <Dialog
      open={open}
      onClose={close}
      title="New agent"
      footer={
        <>
          <Btn variant="outline" onPanel onClick={close}>
            Cancel
          </Btn>
          <Btn variant="white" disabled={!name.trim()} onClick={create}>
            Add agent
          </Btn>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Name">
          <input autoFocus value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && create()} className={inputCls} />
        </Field>
        <Field label="Label" hint="optional">
          <input value={tag} onChange={(e) => setTag(e.target.value)} className={inputCls} />
        </Field>
        <Field label="Description">
          <textarea value={about} onChange={(e) => setAbout(e.target.value)} rows={3} placeholder="Who it is and what you want from it, in your own words." className={cx(inputCls, 'h-auto resize-none py-2 leading-[20px]')} />
        </Field>
        <Field label="Face">
          <Faces value={palette} onChange={setPalette} onPanel />
        </Field>
      </div>
    </Dialog>
  )
}

export function AddGroup() {
  const which = useDemo((s) => s.groupDialog)
  const open = !!which
  const setS = useDemo((s) => s.set)
  const add = useDemo((s) => s.addGroup_)
  const agents = useDemo((s) => s.agents)
  const [name, setName] = useState('')
  const [members, setMembers] = useState<string[]>([])
  useEffect(() => {
    // (opened from an agent, the chat starts with that agent in it)
    if (which) (setName(''), setMembers(which.startsWith('new:') ? [which.slice(4)] : []))
  }, [which])
  const close = () => setS({ groupDialog: null })
  const create = () => name.trim() && add(name.trim(), members)
  return (
    <Dialog
      open={open}
      onClose={close}
      title="New group chat"
      footer={
        <>
          <Btn variant="outline" onPanel onClick={close}>
            Cancel
          </Btn>
          <Btn variant="white" disabled={!name.trim()} onClick={create}>
            Create group chat
          </Btn>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Name">
          <input autoFocus value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && create()} className={inputCls} />
        </Field>
        <Field label="Members" hint={members.length ? `${members.length} chosen` : undefined}>
          <div className="flex flex-wrap gap-2">
            {agents.map((a) => (
              <Chip key={a.id} on={members.includes(a.id)} onClick={() => setMembers((v) => (v.includes(a.id) ? v.filter((x) => x !== a.id) : [...v, a.id]))}>
                <Glyph palette={a.palette} />
                {a.name}
              </Chip>
            ))}
          </div>
        </Field>
      </div>
    </Dialog>
  )
}

/** The agents screen: a talk between two agents if one is open, else the chosen agent's (or group's) empty page or conversation. */
export function AgentsView() {
  const { id, agent, group } = useTarget()
  const open = useDemo((s) => s.current[id])
  const wireId = useDemo((s) => s.wireId)
  if (wireId) return <WireView id={wireId} />
  if (!agent && !group)
    return (
      <Empty
        icon={<Glyph palette="syntax" size={28} />}
        title="Who works here?"
        action={
          <Btn variant="white" icon={Plus} className="px-3" onClick={() => useDemo.getState().set({ addAgent: true })}>
            Add an agent
          </Btn>
        }
      >
        This workspace has no agents yet. Give the first one a name and say what it is for; it keeps its own memory and its own conversations from then on.
      </Empty>
    )
  return open ? <Thread agent={agent} group={group} /> : <AgentHome />
}
